import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Plus, Calendar, DollarSign, Clock, AlertCircle,
  CheckCircle, PlayCircle, Folder, Target, Activity, ChevronRight
} from 'lucide-react';
import { Project, Client, NewProject } from '../../../types';
import { useAuth } from '../../../hooks';
import api from '../../../services/api';
import { ProjectForm } from '../ProjectForm/ProjectForm';
import { Button, Badge, Card, EmptyState, LoadingSpinner, PageHeader } from '../../common';

// Projects List Component
export const ProjectsList: React.FC = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [clientFilter, setClientFilter] = useState<string>('all');
  const [showNewProjectForm, setShowNewProjectForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const { token } = useAuth();

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      const [projectsResponse, clientsResponse] = await Promise.all([
        api.get<any>('/api/projects', token || undefined),
        api.get<any>('/api/clients', token || undefined)
      ]);

      const projectsArray = Array.isArray(projectsResponse) ? projectsResponse : projectsResponse.projects || [];
      const clientsArrayRaw = Array.isArray(clientsResponse) ? clientsResponse : clientsResponse.clients || [];

      // Transform backend client data to frontend format
      const clientsArray = clientsArrayRaw.map((client: any) => ({
        ...client,
        name: client.company_name || client.name
      }));


      // Transform backend data to frontend format
      const transformedProjects = projectsArray.map((project: any) => ({
        id: project.id,
        name: project.name,
        description: project.description,
        clientId: project.client_id,
        projectType: project.project_type,
        status: project.status,
        priority: project.priority,
        startDate: project.start_date,
        dueDate: project.end_date,
        estimatedHours: project.estimated_hours,
        actualHours: project.actual_hours || 0,
        budget: project.budget ? Number(project.budget) : 0,
        hourlyRate: project.hourly_rate,
        createdBy: project.created_by,
        createdAt: project.created_at,
        updatedAt: project.updated_at,
        notes: project.notes
      }));

      // Map client data to projects
      const projectsWithClients = transformedProjects.map((project: Project) => ({
        ...project,
        client: clientsArray.find((client: Client) => client.id === project.clientId)
      }));

      setProjects(projectsWithClients);
      setClients(clientsArray);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
      console.error('Error details:', error);
      setProjects([]);
      setClients([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const handleCreateProject = async (projectData: NewProject) => {
    try {
      setCreating(true);

      const backendData = {
        client_id: projectData.clientId,
        name: projectData.name,
        description: projectData.description || '',
        project_type: null,
        status: projectData.status,
        priority: 'medium',
        start_date: projectData.startDate || null,
        end_date: projectData.dueDate || null,
        budget: (projectData.budget || 0) > 0 ? projectData.budget : null,
        estimated_hours: (projectData.estimatedHours || 0) > 0 ? projectData.estimatedHours : null,
        project_manager_id: null,
        notes: ''
      };

      await api.post<any>('/api/projects', backendData, token || undefined);
      setShowNewProjectForm(false);
      fetchProjects();
    } catch (error) {
      console.error('Failed to create project:', error);
      alert('Failed to create project. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch(status) {
      case 'in_progress': return <PlayCircle className="w-3 h-3" />;
      case 'planning': return <Clock className="w-3 h-3" />;
      case 'review': return <CheckCircle className="w-3 h-3" />;
      case 'on_hold': return <AlertCircle className="w-3 h-3" />;
      case 'completed': return <CheckCircle className="w-3 h-3" />;
      default: return null;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch(priority) {
      case 'urgent': return 'text-red-600 bg-red-50';
      case 'high': return 'text-orange-600 bg-orange-50';
      case 'medium': return 'text-yellow-600 bg-yellow-50';
      case 'low': return 'text-green-600 bg-green-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const formatBudget = (value: number) => {
    if (value === 0) return '-';
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value}`;
  };

  const getDaysUntilDue = (dueDate: string | null) => {
    if (!dueDate) return '-';
    const due = new Date(dueDate);
    const today = new Date();
    const diffTime = due.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return <span className="text-red-600 font-medium">Overdue</span>;
    if (diffDays === 0) return <span className="text-orange-600 font-medium">Today</span>;
    if (diffDays <= 7) return <span className="text-amber-600">{diffDays}d left</span>;
    return `${diffDays}d left`;
  };

  // Calculate statistics
  const stats = {
    total: projects.length,
    inProgress: projects.filter(p => p.status === 'in_progress').length,
    planning: projects.filter(p => p.status === 'planning').length,
    totalBudget: projects.reduce((sum, p) => sum + (p.budget || 0), 0),
    totalSpent: projects.reduce((sum, p) => sum + (p.actualHours || 0) * (p.hourlyRate || 0), 0),
    avgProgress: projects.length > 0 ? Math.round(projects.reduce((sum, p) => {
      // Calculate progress based on hours if available
      const progress = (p.estimatedHours && p.actualHours)
        ? Math.min((p.actualHours / p.estimatedHours) * 100, 100)
        : p.status === 'completed' ? 100
        : p.status === 'in_progress' ? 50
        : p.status === 'planning' ? 10
        : 0;
      return sum + progress;
    }, 0) / projects.length) : 0
  };

  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                     project.client?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                     project.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || project.status === statusFilter;
    const matchesClient = clientFilter === 'all' || project.clientId.toString() === clientFilter;
    return matchesSearch && matchesStatus && matchesClient;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <LoadingSpinner message="Loading projects..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Projects"
        subtitle="Track and manage your project portfolio"
        actions={
          <Button
            variant="primary"
            size="md"
            icon={<Plus className="w-4 h-4" />}
            onClick={() => setShowNewProjectForm(true)}
          >
            New Project
          </Button>
        }
      />

      <div className="px-6 py-6">
        {/* Inline Stats Cards - 6 columns */}
        <div className="grid grid-cols-6 gap-4 mb-6">
            <Card variant="grayCompact">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 font-medium">Total Projects</p>
                  <p className="text-xl font-semibold text-gray-900 mt-0.5">{stats.total}</p>
                </div>
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Folder className="w-4 h-4 text-blue-600" />
                </div>
              </div>
            </Card>

            <Card variant="grayCompact">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 font-medium">In Progress</p>
                  <p className="text-xl font-semibold text-gray-900 mt-0.5">{stats.inProgress}</p>
                </div>
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <PlayCircle className="w-4 h-4 text-blue-600" />
                </div>
              </div>
            </Card>

            <Card variant="grayCompact">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 font-medium">Planning</p>
                  <p className="text-xl font-semibold text-gray-900 mt-0.5">{stats.planning}</p>
                </div>
                <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Clock className="w-4 h-4 text-purple-600" />
                </div>
              </div>
            </Card>

            <Card variant="grayCompact">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 font-medium">Total Budget</p>
                  <p className="text-xl font-semibold text-gray-900 mt-0.5">{formatBudget(stats.totalBudget)}</p>
                </div>
                <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-4 h-4 text-emerald-600" />
                </div>
              </div>
            </Card>

            <Card variant="grayCompact">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 font-medium">Spent</p>
                  <p className="text-xl font-semibold text-gray-900 mt-0.5">{formatBudget(stats.totalSpent)}</p>
                </div>
                <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Target className="w-4 h-4 text-amber-600" />
                </div>
              </div>
            </Card>

            <Card variant="grayCompact">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 font-medium">Avg Progress</p>
                  <p className="text-xl font-semibold text-gray-900 mt-0.5">{stats.avgProgress}%</p>
                </div>
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                  <Activity className="w-4 h-4 text-green-600" />
                </div>
              </div>
            </Card>
          </div>

          {/* Search and Filters */}
          <div className="flex items-center gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search projects..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="text-sm px-3 py-2 bg-white border border-gray-300 rounded-lg"
            >
              <option value="all">All Status</option>
              <option value="in_progress">In Progress</option>
              <option value="planning">Planning</option>
              <option value="review">Review</option>
              <option value="on_hold">On Hold</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>

            <select
              value={clientFilter}
              onChange={(e) => setClientFilter(e.target.value)}
              className="text-sm px-3 py-2 bg-white border border-gray-300 rounded-lg"
            >
              <option value="all">All Clients</option>
              {clients.map(client => (
                <option key={client.id} value={client.id}>{client.name}</option>
              ))}
            </select>
          </div>

        {/* Projects Table */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mt-6">
          {filteredProjects.length > 0 ? (
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                <tr>
                  <th className="text-left py-3 px-6 text-xs font-medium text-gray-600 uppercase tracking-wider">Project</th>
                  <th className="text-left py-3 px-6 text-xs font-medium text-gray-600 uppercase tracking-wider">Client</th>
                  <th className="text-left py-3 px-6 text-xs font-medium text-gray-600 uppercase tracking-wider">Status</th>
                  <th className="text-left py-3 px-6 text-xs font-medium text-gray-600 uppercase tracking-wider">Progress</th>
                  <th className="text-left py-3 px-6 text-xs font-medium text-gray-600 uppercase tracking-wider">Priority</th>
                  <th className="text-left py-3 px-6 text-xs font-medium text-gray-600 uppercase tracking-wider">Due Date</th>
                  <th className="text-left py-3 px-6 text-xs font-medium text-gray-600 uppercase tracking-wider">Budget</th>
                  <th className="text-left py-3 px-6"></th>
                </tr>
              </thead>
              <tbody>
                {filteredProjects.map((project) => {
                  const progress = (project.estimatedHours && project.actualHours)
                    ? Math.min((project.actualHours / project.estimatedHours) * 100, 100)
                    : project.status === 'completed' ? 100
                    : project.status === 'in_progress' ? 50
                    : project.status === 'planning' ? 10
                    : 0;
                  const priority = 'medium'; // Placeholder priority

                  return (
                    <tr
                      key={project.id}
                      className="border-b border-gray-100 hover:bg-gray-50 transition-colors group cursor-pointer"
                      onClick={() => navigate(`/projects/${project.id}`)}
                    >
                      <td className="py-3 px-6">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
                            <Folder className="w-4 h-4 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{project.name}</p>
                            <p className="text-xs text-gray-500">{project.description?.substring(0, 40)}...</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-6">
                        <span className="text-sm text-gray-700">{project.client?.name}</span>
                      </td>
                      <td className="py-3 px-6">
                        <Badge status={project.status} icon={getStatusIcon(project.status)} />
                      </td>
                      <td className="py-3 px-6">
                        <div className="flex items-center gap-3">
                          <div className="flex-1 bg-gray-200 rounded-full h-2 max-w-[100px]">
                            <div
                              className={`h-2 rounded-full transition-all ${
                                progress >= 75 ? 'bg-green-500' :
                                progress >= 50 ? 'bg-blue-500' :
                                progress >= 25 ? 'bg-yellow-500' :
                                'bg-gray-400'
                              }`}
                              style={{ width: `${progress}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium text-gray-700">{Math.round(progress)}%</span>
                        </div>
                      </td>
                      <td className="py-3 px-6">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getPriorityColor(priority)}`}>
                          {priority}
                        </span>
                      </td>
                      <td className="py-3 px-6">
                        <div className="flex items-center gap-1.5 text-sm">
                          <Calendar className="w-3.5 h-3.5 text-gray-400" />
                          {project.dueDate ? (
                            <div>
                              <div>{new Date(project.dueDate).toLocaleDateString()}</div>
                              <div className="text-xs">{getDaysUntilDue(project.dueDate)}</div>
                            </div>
                          ) : (
                            <span className="text-gray-400">No due date</span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-6">
                        <p className="font-medium text-gray-900">{formatBudget(project.budget || 0)}</p>
                      </td>
                      <td className="py-3 px-6">
                        <ChevronRight className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <EmptyState
              icon={Folder}
              title="No projects found"
              description={
                searchTerm || statusFilter !== 'all' || clientFilter !== 'all'
                  ? 'Try adjusting your search or filters.'
                  : 'Get started by creating your first project.'
              }
              action={
                !searchTerm && statusFilter === 'all' && clientFilter === 'all' ? (
                  <Button
                    variant="primary"
                    size="md"
                    icon={<Plus className="h-4 w-4" />}
                    onClick={() => setShowNewProjectForm(true)}
                  >
                    New Project
                  </Button>
                ) : undefined
              }
            />
          )}
        </div>
      </div>

      {showNewProjectForm && (
        <ProjectForm
          onClose={() => setShowNewProjectForm(false)}
          onSubmit={handleCreateProject}
          loading={creating}
          clients={clients}
        />
      )}
    </div>
  );
};

export default ProjectsList;
